import {
  ArrowUpDown,
  Menu as MenuIcon,
  RefreshCcw,
  Settings2,
  Share2,
  DollarSign as TransactionIcon,
  Trash2
} from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { Menu } from '@unistocks/components/menu/menu';
import { HeaderIcon } from '@unistocks/components/modal/header-icon';
import { HeaderButton } from '@unistocks/components/page/header/header-button/header-button';
import { FullScreenSheet } from '@unistocks/components/sheet/full-screen-sheet';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { toast } from '@unistocks/components/toast';
import { PortfolioSettings } from '@unistocks/features/portfolios/components/portfolio-settings/settings';
import { SortPortfolio } from '@unistocks/features/portfolios/components/portfolio-settings/sort/sort';
import { SharableLink } from '@unistocks/features/portfolios/components/sharable-links/sharable-link';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { isPortfolio as isPortfolioCheck } from '@unistocks/features/portfolios/portfolio-utils';
import { PortfolioActions } from '@unistocks/features/portfolios/store/portfolio.slice';
import { WatchlistActions } from '@unistocks/features/watchlists/store/watchlist.slice';
import { t } from '@unistocks/localization';
import {
  portfoliosGetListQueryKey,
  usePortfoliosDeleteMutation
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  useWatchlistsDeleteMutation,
  watchlistsGetListQueryKey
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import { useAppDispatch } from '@unistocks/shared/hooks/redux-hooks';
import { useToggle } from '@unistocks/shared/hooks/use-toggle';
import { useRouter } from 'expo-router';
import { memo, useCallback, useState } from 'react';

export type PortfolioMenuProps = {
  portfolio: PortfolioBase;
};

export const PortfolioMenu = memo<PortfolioMenuProps>(({ portfolio }) => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [shareSettingsOpen, toggleShareSettingsOpen, setShareSettingsOpen] = useToggle();
  const [portfolioSettings, setPortfolioSettings] = useState<'settings' | 'sorting'>();
  const isPortfolio = isPortfolioCheck(portfolio);

  const navigateToTransactions = useCallback(() => {
    router.navigate({
      pathname: '/(tabs)/portfolios/[id]/transactions',
      params: { id: portfolio.id }
    });
  }, [portfolio.id, router]);

  const navigateToExternalAccounts = useCallback(() => {
    router.navigate(
      {
        pathname: '/(tabs)/(menu)/external-accounts'
      },
      {
        withAnchor: true
      }
    );
  }, [router]);

  const { mutate: deletePortfolio, isPending: isDeletePortfolioLoading } =
    usePortfoliosDeleteMutation(portfolio.id, {
      onSuccess: () => {
        toast.success(t('::SuccessfullyDeletedPortfolio', { 0: portfolio?.name }));

        dispatch(PortfolioActions.setSelectedPortfolioId(undefined));
        queryClient.invalidateQueries({ queryKey: portfoliosGetListQueryKey() });
        router.replace('/(tabs)');
      }
    });

  const { mutate: deleteWatchlist, isPending: isDeleteWatchlistLoading } =
    useWatchlistsDeleteMutation(portfolio.id, {
      onSuccess: () => {
        toast.success(t('::SuccessfullyDeletedWatchlist', { 0: portfolio.name }));

        dispatch(WatchlistActions.setSelectedWatchlistId(undefined));
        queryClient.invalidateQueries({ queryKey: watchlistsGetListQueryKey() });
        router.replace('/(tabs)');
      }
    });

  const isLoading = isDeletePortfolioLoading || isDeleteWatchlistLoading;

  return (
    <>
      <Menu
        trigger={<HeaderButton Icon={MenuIcon} />}
        headerTitle={portfolio.name}
        headerIcon={<UnistocksCircle rounded size={'$2.5'} />}
      >
        <Menu.Item
          disabled={isLoading}
          icon={<Settings2 color={'$unistocks'} />}
          title={t('::EditWhat', { 0: portfolio.name })}
          onPress={() => {
            setSettingsOpen(true);
            setPortfolioSettings('settings');
          }}
        />
        <Menu.Item
          disabled={isLoading}
          icon={<ArrowUpDown color={'$unistocks'} />}
          title={t('::Portfolio:ManualSort')}
          onPress={() => {
            setSettingsOpen(true);
            setPortfolioSettings('sorting');
          }}
        />
        {isPortfolio ? (
          <Menu.Item
            disabled={isLoading}
            icon={<RefreshCcw color={'$purple10'} />}
            title={t('::ExternalAccounts')}
            onPress={navigateToExternalAccounts}
          />
        ) : null}
        {isPortfolio ? (
          <Menu.Item
            disabled={isLoading}
            icon={<TransactionIcon color={'$green11'} />}
            title={t('::ShowWhat', { 0: t('::Transactions').toLocaleLowerCase() })}
            onPress={navigateToTransactions}
          />
        ) : null}
        {isPortfolio ? (
          <Menu.Item
            disabled={isLoading}
            icon={<Share2 color={'$headingFont'} />}
            title={t('::Portfolio:Share')}
            onPress={toggleShareSettingsOpen}
          />
        ) : null}
        <Confirm
          message={t('::Portfolio:Delete', { 0: portfolio?.name })}
          requiredInputConfirmation={portfolio.name}
          onConfirm={isPortfolio ? deletePortfolio : deleteWatchlist}
        >
          <Menu.Item
            closeOnPress={false}
            disabled={isLoading}
            icon={<Trash2 color={'$danger'} />}
            iconAfter={isLoading ? <Spinner /> : undefined}
            title={t('::DeleteWhat', {
              0: (isPortfolio ? t('::Portfolio') : t('::Watchlist')).toLocaleLowerCase()
            })}
          />
        </Confirm>
      </Menu>

      <FullScreenSheet
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        headerTitle={
          portfolioSettings === 'settings'
            ? t('::EditWhat', { 0: portfolio.name })
            : t('::Portfolio:ManualSort')
        }
        headerIcon={
          portfolioSettings === 'settings' ? (
            <UnistocksCircle rounded size={'$2.5'} />
          ) : (
            <HeaderIcon Icon={ArrowUpDown} />
          )
        }
        headerProps={{ mb: portfolioSettings === 'settings' ? undefined : 0 }}
      >
        {portfolioSettings === 'settings' ? (
          <PortfolioSettings portfolio={portfolio} onSave={() => setSettingsOpen(false)} />
        ) : (
          <SortPortfolio
            portfolio={portfolio}
            onSave={() => setSettingsOpen(false)}
            onCancel={() => setSettingsOpen(false)}
          />
        )}
      </FullScreenSheet>

      <Sheet
        open={shareSettingsOpen}
        onOpenChange={setShareSettingsOpen}
        enableDynamicSizing
        scrollMode="view"
        contentTopMargin
        headerTitle={t('::Portfolio:Share')}
        headerIcon={<HeaderIcon Icon={Share2} />}
      >
        <SharableLink portfolioId={portfolio.id} />
      </Sheet>
    </>
  );
});

PortfolioMenu.displayName = 'PortfolioMenu';
